import type { PageEntry, Entry, Node } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import { assessDateRange } from 'Common/util/date';
import dynamic from 'next/dynamic';

import { useTheme } from 'styled-components';

import type { Props } from './Listing';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    articles: Entry[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors, articles } = data;

    const { title, sys, heroDescription, heroCTA, signUpPromo } = entry;

    const { colors } = useTheme();

    const dateAssessedArticles = articles.map(article => {
        const assessedDateStatus = assessDateRange(article.date);

        return {
            heading: article.articleTitle,
            image: cmsParser.image(article.articleMainHeaderImage),
            calendarColour:
                assessedDateStatus === 'past'
                    ? colors.accents[7].hex
                    : assessedDateStatus === 'current'
                    ? colors.primary.hex
                    : colors.accents[2].hex,
            weekStatus:
                assessedDateStatus === 'current'
                    ? { status: 'Current week', colour: '#B2000E' }
                    : undefined,
            week: article.weekNumber,
            description: article.articleSummary,
            href: article.sys.uri,
            categories: article.articleCategory
        };
    });

    const currentWeekArticleIndex = dateAssessedArticles.findIndex(
        article => article.weekStatus?.status === 'Current week'
    );

    const allArticleCategories: string[] = [];

    dateAssessedArticles.map(article => {
        allArticleCategories.push(...article.categories);
    });

    const allUniqueArticleCategories = [...new Set(allArticleCategories.sort())];

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            id: '52-weeks-hero-banner',
            heading: {
                number: '52',
                subheading: 'WEEKS TO GO'
            },
            text: heroDescription,
            link: heroCTA
                ? {
                      ...cmsParser.linkButton(heroCTA),
                      icon: dynamic(() =>
                          import('@solent-university/solent-icons').then(
                              icons => icons[heroCTA.icon.name]
                          )
                      ),
                      variant: 'accentTwoSolid'
                  }
                : undefined
        },
        featuredCard: {
            ...dateAssessedArticles[currentWeekArticleIndex],
            weekStatus: { status: 'Current week', colour: '#B2000E' },
            link: {
                href: dateAssessedArticles[currentWeekArticleIndex].href,
                label: 'Read article'
            }
        },
        listing: {
            id: '52-weeks-article-listing',
            articles: dateAssessedArticles.map((article, index) => ({
                ...article,
                weekStatus:
                    index === currentWeekArticleIndex
                        ? { status: 'Current week', colour: '#B2000E' }
                        : index === currentWeekArticleIndex - 1
                        ? { status: 'Previous week', colour: '#197A6E' }
                        : index === currentWeekArticleIndex + 1
                        ? { status: 'Upcoming week', colour: '#7536C3' }
                        : undefined
            })),
            loadPreviousButton: {
                heading: 'View previous weeks',
                button: {
                    label: 'Load previous',
                    ariaLabel: 'Load previous articles',
                    variant: 'loadPreviousSecondary'
                }
            },
            loadMoreButton: {
                label: 'Load more',
                ariaLabel: 'Load more articles',
                variant: 'tertiaryWithHover'
            },
            categories: ['All weeks', ...allUniqueArticleCategories]
        },
        promo: {
            icon: dynamic(() =>
                import('@solent-university/solent-icons').then(
                    icons => icons[signUpPromo.icon.name]
                )
            ),
            heading: signUpPromo.heading,
            text: signUpPromo.description,
            backgroundColour: colors.accents[2].hex,
            link: signUpPromo.button
                ? { ...cmsParser.linkButton(signUpPromo.button), variant: 'accentTwoTextWhiteBg' }
                : undefined
        }
    };
};

export default datamap;
